import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import './AboutUs.css';

const founders = [
  {
    id: 1,
    name: 'Deependra Kumar',
    title: 'Co-Founder & CTO',
    bio: 'Deependra Kumar is co-author of the Emerging World Models structural framework and the HLLSet Algebra program, and architect of the state-machine codebase (12 crates, 240 automated tests) and the platform proofs-of-concept. He brings 25 years of enterprise application development and architecture expertise and holds 6 patents in AI and Robotics. At Codentic AI he leads technical product development, bridging research and engineering to deliver platforms that create measurable business impact.',
    expertise: ['Technical Product Management', 'AI & Robotics Patents', 'Application Development', 'Enterprise Solutions', 'Agile Delivery'],
    initials: 'DK',
  },
  {
    id: 2,
    name: 'Alex Mylnikov',
    title: 'Co-Founder & Chief AI Officer',
    bio: 'Alex Mylnikov is lead author of the Emerging World Models consolidation document — the four principles, the Ashby–Bootstrap correspondence and the Grothendieck-topology target — including the Noether evolution law and its conserved quantities. With 40 years of IT R&D experience and 10 patents in AI, he has architected mission-critical systems across industries. As Chief AI Officer at Codentic AI, Alex owns the mathematics that makes the guarantees hold.',
    expertise: ['Machine Learning', 'Cloud Infrastructure', 'MLOps', 'System Architecture', 'NLP & LLMs', 'Robotics (ROS 2)', 'AI Research', 'Data Pipelines'],
    initials: 'AM',
  },
  {
    id: 3,
    name: 'Aaryan Chandresh Madani',
    title: 'Global Head of Business Expansion',
    bio: 'Aaryan Chandresh Madani is a globally respected business leader with 10 years of international business development experience spanning multiple continents, industries, and market cycles. He has opened new markets, structured landmark partnerships, and built enterprise relationships at the highest levels of global commerce. At Codentic AI, Chandresh leads the company\'s worldwide expansion strategy, positioning Codentic AI as a trusted AI partner for enterprises across the globe.',
    expertise: ['Global Market Expansion', 'International Business', 'Strategic Alliances', 'Revenue Growth', 'Partnership Development', 'AI Healthcare'],
    initials: 'CM',
  },
  {
    id: 4,
    name: 'Dr. Hari Kumar Singh',
    title: 'CEO & Director of AI Research',
    bio: 'Dr. Hari Kumar Singh is a distinguished scientist and executive with 40 patents across artificial intelligence and multiple fields of science and technology. His intellectual contributions have shaped research agendas and product directions across the global AI industry. As CEO and Director of AI Research at Codentic AI, Dr. Singh sets the company\'s strategic and scientific direction, ensuring every platform the company builds is grounded in rigorous research, responsible innovation, and real-world applicability.',
    expertise: ['AI Research & Innovation', 'Deep Learning', 'Computational AI', 'AI Ethics & Governance', 'Academic Partnerships'],
    initials: 'HS',
  },
  {
    id: 5,
    name: 'Sumitabh Singh',
    title: 'Head of Engineering',
    bio: 'An alumnus of the Indian Institute of Technology (IIT), Sumitabh Singh brings the rigour of one of the world\'s most prestigious engineering institutions to every system he builds. His first-principles thinking and deep technical expertise drive engineering excellence across Codentic AI\'s entire product portfolio. Sumitabh leads the engineering organisation, setting architectural standards, championing best practices, and ensuring the company\'s platforms are built to perform at enterprise scale.',
    expertise: ['IIT Engineering', 'Full-Stack Development', 'Scalable Systems', 'DevOps & CI/CD', 'API Engineering', 'Team Leadership'],
    initials: 'SK',
  },
  {
    id: 6,
    name: 'Nidhi Sinha',
    title: 'Head of Business Development & Sales',
    bio: 'Nidhi Sinha is a dynamic business leader who drives Codentic AI\'s revenue growth and client acquisition strategy. With a sharp commercial instinct and a talent for building lasting relationships, she leads go-to-market execution, forges high-value enterprise partnerships, and expands the company\'s presence across key markets. Her work ensures that Codentic AI\'s innovations reach the organisations that need them most.',
    expertise: ['Business Development', 'Enterprise Sales', 'Client Partnerships', 'Go-to-Market Strategy', 'Revenue Growth'],
    initials: 'NI',
  },
];

// The documents name exactly two co-founders. Everyone else is presented as
// leadership, so the About page and the investor narrative agree.
const CO_FOUNDERS = founders.filter((f) => [1, 2].includes(f.id));
const LEADERSHIP = founders.filter((f) => ![1, 2].includes(f.id));

function FounderCard({ founder }) {
  return (
    <div className="founder-card">
      <div className="founder-avatar">
        <span>{founder.initials}</span>
      </div>
      <div className="founder-info">
        <h3>{founder.name}</h3>
        <span className="founder-title">{founder.title}</span>
        <p className="founder-bio">{founder.bio}</p>
        <div className="founder-expertise">
          {founder.expertise.map((skill) => (
            <span key={skill} className="expertise-tag">{skill}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function AboutUs() {
  return (
    <div className="about-page">
      <SEO {...getSEOData('about')} />

      {/* Hero */}
      <section className="about-hero">
        <div className="container">
          <h1>About <span className="gradient-text">Codentic AI</span></h1>
          <p>Applied AI research and product company. We add a structural memory and working-context layer beside any AI model — the host keeps its weights, and the memory can be measured, verified and carried across models.</p>
        </div>
      </section>

      {/* Mission */}
      <section className="about-mission">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>To accelerate enterprise transformation through AI, delivering research-grade innovation as production-ready products.</p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">🔬</div>
              <h3>Our Approach</h3>
              <p>We combine deep research with pragmatic engineering to build AI platforms that are robust, explainable, and immediately deployable.</p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">🌍</div>
              <h3>Our Impact</h3>
              <p>From an exact round-trip on a real vision-language-action stack to a CPU-only document platform serving 55k+ knowledge cards, our research is measured on real models — and shipped.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="about-founders">
        <div className="container">
          <div className="section-header">
            <h2>Meet the <span className="gradient-text">Founders</span></h2>
            <p>The two co-founders who built the framework and the codebase behind Emerging World Models.</p>
          </div>

          <div className="founders-grid">
            {CO_FOUNDERS.map((founder) => (
              <FounderCard key={founder.id} founder={founder} />
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section
        className="about-founders"
        style={{ background: 'var(--light-gray, #f5f5f5)' }}
      >
        <div className="container">
          <div className="section-header">
            <h2>Leadership</h2>
            <p>
              The team carrying the platforms, partnerships and delivery — alongside a deliberately
              small research and engineering core.
            </p>
          </div>

          <div className="founders-grid">
            {LEADERSHIP.map((person) => (
              <FounderCard key={person.id} founder={person} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
